import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

import { useTableAllocationStore } from "@/data/stores/tableAllocation";

import type { IdentityChangedEventData } from "@/data/events/events";
import type { IProfile } from "@/data/model/Profile";
import { role } from "@/data/util/Config";

import { objectToArray } from "@/shared/utils/mappers";

export const useIdentityStore = defineStore("identity", () => {
    const allocation = useTableAllocationStore();

    // state
    const profile = ref<IProfile | undefined>(undefined);

    // getters
    const identityId = computed(() => profile.value?.identityId);
    const roles = computed(() => objectToArray(profile.value?.roles));
    const operatorLocations = computed(() => objectToArray(profile.value?.operatorLocations));
    const isAdmin = computed(() => roles.value.includes(role.Admin));
    const isWaiterInCurrentLocation = computed(
        () => allocation.locationId !== undefined && isWaiterInLocation(allocation.locationId)
    );
    const hasOperatorAccess = computed(() => isAdmin.value || isWaiterInCurrentLocation.value);

    // actions
    function onIdentityChanged(eventData: IdentityChangedEventData) {
        profile.value = eventData.profile;
    }

    function setIdentity(profileInfo: IProfile) {
        profile.value = profileInfo;
    }

    function isWaiterInLocation(locationId: string): boolean {
        return roles.value.includes(role.Operator) && operatorLocations.value.includes(locationId);
    }

    return {
        // state
        identityId,
        roles,
        operatorLocations,
        profile,

        // getters
        isAdmin,
        isWaiterInCurrentLocation,
        hasOperatorAccess,

        // actions
        setIdentity,
        onIdentityChanged,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useIdentityStore, import.meta.hot));
}
